import React, { useState } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { buildImageUrl, imageDisplayName } from '../../components/image-utils';
import { DeleteAction, EditAction, PublicationAction } from '../../../../components/row-actions';
import { TIPO_BANNER_LABEL } from '../../../../model/apm/apm-enums';
import { IBannerResponse } from '../../../../model/apm/banner/response/banner-response';
import { BannerDisplayStatus, formatBannerPeriod, getBannerDisplayStatus } from './banner-utils';

const PERIOD_CHIP: Record<BannerDisplayStatus, { label: string; color: 'default' | 'info' | 'success' | 'warning' } | undefined> = {
    permanent: undefined,
    active: { label: 'Em exibição', color: 'success' },
    scheduled: { label: 'Agendado', color: 'info' },
    expired: { label: 'Expirado', color: 'warning' },
};

interface BannerRowProps {
    banner: IBannerResponse;
    dragHandle: React.ReactNode;
    onEdit: () => void;
    onDelete: () => void;
    onTogglePublication: () => void;
}

export function BannerRow({ banner, dragHandle, onEdit, onDelete, onTogglePublication }: BannerRowProps) {
    const previewUrl = buildImageUrl(banner.image);
    const [failedUrl, setFailedUrl] = useState<string>();
    const periodChip = PERIOD_CHIP[getBannerDisplayStatus(banner)];

    return (
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, pr: 0.5 }}>
            {dragHandle}
            <Box
                sx={{
                    width: 96, height: 54, flexShrink: 0, borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default',
                    border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                {previewUrl && failedUrl !== previewUrl
                    ? <Box component="img" src={previewUrl} alt={banner.title} onError={() => setFailedUrl(previewUrl)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <ImageIcon color="disabled" />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, opacity: banner.isPublished ? 1 : 0.7 }}>
                <Typography variant="body2" fontWeight={500} noWrap>{banner.title}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {formatBannerPeriod(banner)}{banner.url ? ` · ${banner.url}` : ''}{banner.image && (!previewUrl || failedUrl === previewUrl) ? ` · ${imageDisplayName(banner.image)}` : ''}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Chip size="small" color={banner.isPublished ? 'success' : 'default'} label={banner.isPublished ? 'Publicado' : 'Rascunho'} />
                    <Chip size="small" color="primary" variant="outlined" label={TIPO_BANNER_LABEL[banner.type] || '-'} />
                    {periodChip && <Chip size="small" variant="outlined" color={periodChip.color} label={periodChip.label} />}
                    {banner.showOnSep && <Chip size="small" variant="outlined" label="Exibe no SEP" />}
                </Stack>
            </Box>
            <PublicationAction isPublished={banner.isPublished} onClick={onTogglePublication} />
            <EditAction onClick={onEdit} />
            <DeleteAction onClick={onDelete} />
        </Paper>
    );
}
